import {
  type ImageMedia,
  type VideoMedia,
  type OPProductConnection,
} from "@root/tablescape/state";
import {
  type Filter,
  type CountryCode,
  type ProductCollectionSortKeys,
  type Maybe,
  type Image,
  type Collection,
} from "@shopify/hydrogen-react/storefront-api-types";

export type CollectionSectionSettings = {
  breadcrumbs: {
    links: {
      links?: {
        links?: { title: string; url: string }[];
        title: string;
        url: string;
      }[];
      title: string;
      url: string;
    }[];
  };
  collectionHero: {
    description: string;
    heroImage: Image;
    title: string;
  };
  collectionId: number;
  meta: {
    localization: { country: CountryCode };
  };
  pagination: {
    pageSize: number;
  };
  settings: {
    version: string;
  };
  text: {
    bestsellersLabel: string;
    breadcrumbsLabel: string;
    featuredLabel: string;
    homeLabel: string;
    loadMoreLabel: string;
    loadingLabel: string;
    newestLabel: string;
    nextLabel: string;
    prevLabel: string;
    priceHighToLowLabel: string;
    priceLowToHighLabel: string;
  };
};

export type CollectionContentCardConnection = {
  background: Maybe<{ value: string }>;
  ctaText: Maybe<{ value: string }>;
  ctaUrl: Maybe<{ value: string }>;
  description: Maybe<{ value: string }>;
  image: Maybe<{ reference: ImageMedia }>;
  mobileImage: Maybe<{ reference: ImageMedia }>;
  title: Maybe<{ value: string }>;
  videoControlColor: Maybe<{ value: string }>;
  videoMp4: Maybe<{ reference: VideoMedia }>;
  videoWebm: Maybe<{ reference: VideoMedia }>;
};

export type CollectionContentCard = {
  background?: string;
  ctaText?: string;
  ctaUrl?: string;
  description?: string;
  image?: ImageMedia;
  mobileImage?: ImageMedia;
  title?: string;
  videoControlColor?: string;
  videoMp4?: Partial<VideoMedia>;
  videoWebm?: Partial<VideoMedia>;
};

type CollectionSortOption = {
  label: { value: string };
  reverse: { value: string };
  sortKey: { value: ProductCollectionSortKeys };
};

export type AdjacentCollectionsConnection = Maybe<{
  references: {
    nodes: {
      handle: Collection["handle"];
      id: Collection["id"];
      title: Collection["title"];
      titleOverride: Maybe<{ value: string }>;
    }[];
  };
}>;

export type CollectionConnection = {
  data?: {
    collection: {
      adjacentCollections: AdjacentCollectionsConnection;
      breadcrumbs: Maybe<{
        references: {
          nodes: {
            onlineStoreUrl: Collection["onlineStoreUrl"];
            title: Collection["title"];
            titleOverride: Maybe<{ value: string }>;
          }[];
        };
      }>;
      contentCards: Maybe<{
        references: {
          nodes: {
            contentCard: { reference: CollectionContentCardConnection };
            id: string;
            position: { value: string };
          }[];
        };
      }>;
      description: Collection["description"];
      descriptionOverride: Maybe<{ value: string }>;
      handle: Collection["handle"];
      heroImage: Maybe<{ reference: { image: Image } }>;
      pageOnScroll: Maybe<{ value: string }>;
      pageSize: Maybe<{ value: string }>;
      products: {
        edges: { node: OPProductConnection }[];
        filters: Filter[];
        pageInfo: {
          endCursor: string;
          hasNextPage: boolean;
        };
      };
      sortOptions: Maybe<{
        references: {
          nodes: CollectionSortOption[];
        };
      }>;
      title: Collection["title"];
      titleOverride: Maybe<{ value: string }>;
    };
  };
};

export type PriceRange = {
  max: number | undefined;
  min: number;
};

export type SortValue = {
  reverse: boolean;
  sortKey: ProductCollectionSortKeys;
};

export const enum SortKeys {
  BEST_SELLING = "BEST_SELLING",
  COLLECTION_DEFAULT = "COLLECTION_DEFAULT",
  CREATED = "CREATED",
  PRICE = "PRICE",
}

export const enum CollectionContentCardType {
  VIDEO_CARD,
  IMAGE_CARD,
  IMAGE_TEXT_CARD,
  TEXT_CARD,
}
